import type { ProfileConfig } from "@worker/config-schema";
import { el, prefersReducedMotion } from "../lib/dom";
import { resolveAsset } from "../lib/assets";

type BgType = ProfileConfig["background"]["type"];
type EffectName = ProfileConfig["background"]["effect"];

/**
 * Background layer. Two-pane design:
 *
 *   base  — image / video / color (or an effect like aurora when
 *           `background.type === "effect"`).
 *   overlay — optional secondary effect stacked on top of the base, so
 *             users can mix an image with rain / snow / stars / grid.
 *
 * Every effect reads its tuning from `background.effectOptions` so the
 * admin's sliders flow directly into the render. Reduced-motion disables
 * animated layers but keeps the composed look.
 */
export function mountBackground(
  bg: ProfileConfig["background"],
  motion: ProfileConfig["motion"],
): HTMLElement {
  const layer = el("div", { class: "bg", "aria-hidden": "true" });
  const opts = bg.effectOptions;
  const shouldReduce =
    motion.respectReducedMotion &&
    motion.reducedMotionSuppresses.includes("backgroundEffect") &&
    prefersReducedMotion();

  // ─── Base layer ───────────────────────────────────────────────────────
  const base = el("div", { class: "bg__layer bg__base", "aria-hidden": "true" });
  layer.append(base);

  if (bg.type === "image" && bg.source) {
    base.classList.add("bg--image");
    base.style.backgroundImage = `url(${JSON.stringify(resolveAsset(bg.source))})`;
    if (opts.imageBlurPx > 0) {
      base.style.filter = `blur(${opts.imageBlurPx}px) brightness(${1 - opts.imageDim})`;
      // Blurring an element shrinks the visible area at its edges — scale up
      // to keep the viewport fully covered.
      base.style.transform = `scale(${1 + opts.imageBlurPx / 60})`;
    } else if (opts.imageDim > 0) {
      base.style.filter = `brightness(${1 - opts.imageDim})`;
    }
  } else if (bg.type === "effect") {
    attachEffect(base, bg.effect, opts, shouldReduce);
  }

  // ─── Overlay effect layer ─────────────────────────────────────────────
  if (bg.overlayEffect && bg.overlayEffect !== "none") {
    const overlay = el("div", { class: "bg__layer bg__overlay-effect", "aria-hidden": "true" });
    attachEffect(overlay, bg.overlayEffect, opts, shouldReduce);
    layer.append(overlay);
  }

  // ─── Color overlay ────────────────────────────────────────────────────
  if (bg.overlay) {
    layer.append(
      el("div", {
        class: "bg__overlay",
        style: `background:${bg.overlay}`,
        "aria-hidden": "true",
      }),
    );
  }

  return layer;
}

// ─── Effect implementations ─────────────────────────────────────────────

function attachEffect(
  host: HTMLElement,
  name: EffectName,
  opts: ProfileConfig["background"]["effectOptions"],
  reduced: boolean,
) {
  host.classList.add(`bg--${name}`);
  if (reduced) host.classList.add("bg--static");

  // CSS custom props give the live rules everything they need.
  host.style.setProperty("--bg-speed", String(opts.speed));
  host.style.setProperty("--bg-tint", opts.tint);
  host.style.setProperty("--bg-dither-scale", `${opts.ditherScale}px`);
  host.style.setProperty("--bg-dither-contrast", String(opts.ditherContrast));

  switch (name) {
    case "rain":
      attachParticles(host, opts.density, "rain", opts.speed);
      break;
    case "snow":
      attachParticles(host, Math.round(opts.density * 0.7), "snow", opts.speed);
      break;
    case "stars":
      attachStars(host, opts.density);
      break;
    // aurora / dither / blur are pure CSS.
  }
}

function attachParticles(
  host: HTMLElement,
  count: number,
  kind: "rain" | "snow",
  speed: number,
) {
  const baseDurSec = kind === "rain" ? 3 : 8;
  for (let i = 0; i < count; i++) {
    // `speed` scales inversely against duration — 2x speed = half duration.
    const dur = (baseDurSec + Math.random() * baseDurSec) / Math.max(0.1, speed);
    const particle = el("span", {
      class: "bg__particle",
      style: `
        left:${(Math.random() * 100).toFixed(2)}%;
        animation-delay:-${(Math.random() * dur).toFixed(2)}s;
        animation-duration:${dur.toFixed(2)}s;
        opacity:${(0.4 + Math.random() * 0.6).toFixed(2)};
      `,
    });
    host.append(particle);
  }
}

function attachStars(host: HTMLElement, count: number) {
  for (let i = 0; i < count; i++) {
    const size = Math.random() < 0.85 ? 1 : Math.random() < 0.5 ? 2 : 3;
    const star = el("span", {
      class: "bg__star",
      style: `
        left:${(Math.random() * 100).toFixed(2)}%;
        top:${(Math.random() * 100).toFixed(2)}%;
        width:${size}px;
        height:${size}px;
        animation-delay:-${(Math.random() * 6).toFixed(2)}s;
        opacity:${(0.4 + Math.random() * 0.6).toFixed(2)};
      `,
    });
    host.append(star);
  }
}

// Kept for callers that only need the base-only signature.
export type BgType_ = BgType;
